const SVG_NAMESPACE = 'http://www.w3.org/2000/svg'

type MapLike<T = unknown> =
  | Map<unknown, T>
  | Record<string, T>
  | {
      get: (key: unknown) => T | undefined
      put: (key: unknown, value: T) => unknown
      [key: string]: unknown
    }
type SvgMessage = {
  itemId?: number | string
  elementId?: string
  objectId?: number | string
  objectTypeId?: string
  objectTitle?: string
  values?: MapLike
}

function mapGet<T>(map: MapLike<T> | null | undefined, key: unknown): T | undefined {
  if (!map) return undefined
  if (map instanceof Map) return map.get(key)
  if (typeof (map as { get?: unknown }).get === 'function') {
    return (map as { get: (key: unknown) => T | undefined }).get(key)
  }
  return (map as Record<string, T>)[String(key)]
}

function mapSet<T>(map: MapLike<T>, key: unknown, value: T): void {
  if (map instanceof Map) map.set(key, value)
  else if (typeof (map as { put?: unknown }).put === 'function') {
    ;(map as { put: (key: unknown, value: T) => unknown }).put(key, value)
  }
  else (map as Record<string, T>)[String(key)] = value
}

function entriesOf<T>(map: MapLike<T> | null | undefined): Array<[string, T]> {
  if (!map) return []
  return map instanceof Map
    ? Array.from(map.entries(), ([key, value]) => [String(key), value])
    : typeof (map as { entries?: unknown }).entries === 'function'
      ? Array.from((map as unknown as { entries: () => Iterable<[unknown, T]> }).entries(), ([key, value]) => [
          String(key),
          value,
        ])
      : Object.entries(map) as Array<[string, T]>
}

function readValue(values: MapLike | undefined, key: string): unknown {
  return mapGet(values, key)
}

function normalizeMapValue(value: unknown): unknown {
  if (value instanceof Map) {
    const source: Record<string, unknown> = {}
    for (const [key, nestedValue] of value.entries()) {
      source[String(key)] = normalizeMapValue(nestedValue)
    }
    return createJavaMap(source)
  }
  if (Array.isArray(value)) return value.map(normalizeMapValue)
  if (!value || typeof value !== 'object') return value
  if (
    typeof (value as { get?: unknown }).get === 'function' &&
    typeof (value as { put?: unknown }).put === 'function'
  ) {
    return value
  }
  const source: Record<string, unknown> = {}
  for (const [key, nestedValue] of Object.entries(value)) {
    source[key] = normalizeMapValue(nestedValue)
  }
  return createJavaMap(source)
}

function createJavaMap(source: Record<string, unknown>): MapLike {
  const target = { ...source } as Record<string, unknown> & {
    get?: (key: unknown) => unknown
    put?: (key: unknown, value: unknown) => unknown
    containsKey?: (key: unknown) => boolean
    isEmpty?: () => boolean
  }
  Object.defineProperties(target, {
    get: {
      enumerable: false,
      value: (key: unknown) => target[String(key)],
    },
    put: {
      enumerable: false,
      value: (key: unknown, value: unknown) => {
        const property = String(key)
        const previous = target[property]
        target[property] = normalizeMapValue(value)
        return previous
      },
    },
    containsKey: {
      enumerable: false,
      value: (key: unknown) => Object.prototype.hasOwnProperty.call(target, String(key)),
    },
    isEmpty: {
      enumerable: false,
      value: () => Object.keys(target).length === 0,
    },
  })
  return target as MapLike
}

function decorateSvgElement(element: Element): any {
  const target = element as any
  if (!target.getOriginalAttributeValue) {
    target.getOriginalAttributeValue = (originalName: string, attributeName: string) => {
      let value = target.getAttribute(originalName)
      if (value == null) {
        value = target.getAttribute(attributeName)
        if (value != null) target.setAttribute(originalName, value)
      }
      return value
    }
    target.setChanged = () => {}
    target.moveElementTo = (
      anchor: Element,
      dx = 0,
      dy = 0,
      xName = 'x',
      yName = 'y',
    ) => {
      const anchorBox = (anchor as SVGGraphicsElement).getBBox?.()
      if (!anchorBox) return
      const root = target.ownerSVGElement as SVGSVGElement | null
      const point = root?.createSVGPoint()
      if (!point) return
      point.x = anchorBox.x + Number(dx || 0)
      point.y = anchorBox.y + Number(dy || 0)
      const rootMatrix = root?.getScreenCTM?.()
      const parentMatrix = target.parentElement?.getScreenCTM?.()?.inverse?.()
      const screenPoint = rootMatrix ? point.matrixTransform(rootMatrix) : point
      const local = parentMatrix ? screenPoint.matrixTransform(parentMatrix) : screenPoint
      if (local) {
        target.setAttribute(xName || 'x', String(local.x))
        target.setAttribute(yName || 'y', String(local.y))
      }
    }
  }
  return target
}

export class SVGContainer {
  private itemParams: MapLike | null = null
  private itemActions: MapLike | null = null
  private readonly elementItems = new Map<string, Set<string>>()
  private readonly objectInfo = new Map<string, Map<string, Record<string, unknown>>>()
  private readonly elementValues = new Map<string, Record<string, unknown>>()
  private readonly objectActionParams = new Map<string, Map<string, Record<string, string>>>()

  constructor(private readonly root: SVGSVGElement) {}

  getItemParamsMap(): MapLike | null {
    return this.itemParams
  }

  getItemParams(key: number | string): unknown {
    return mapGet(this.itemParams, key)
  }

  setItemParamsMap(params: MapLike): void {
    this.itemParams = normalizeMapValue(params) as MapLike
  }

  setItemActions(actions: MapLike): void {
    this.itemActions = normalizeMapValue(actions) as MapLike
  }

  setItemElementObjectActions(message: SvgMessage): void {
    const itemId = String(message.itemId ?? '')
    const elementId = String(message.elementId ?? '')
    if (!elementId) return
    let itemIds = this.elementItems.get(elementId)
    if (!itemIds) this.elementItems.set(elementId, (itemIds = new Set()))
    itemIds.add(itemId)

    const key = `${itemId}\u0000${elementId}`
    if (message.objectId != null && Number(message.objectId) > 0) {
      let objects = this.objectInfo.get(key)
      if (!objects) this.objectInfo.set(key, (objects = new Map()))
      objects.set(String(message.objectId), {
        objectTypeId: message.objectTypeId,
        objectId: Number(message.objectId),
        objectTitle: message.objectTitle,
      })
      const params = readValue(message.values, 'actionParams')
      if (params && typeof params === 'object') {
        let actionParams = this.objectActionParams.get(key)
        if (!actionParams) this.objectActionParams.set(key, (actionParams = new Map()))
        actionParams.set(String(message.objectId), params as Record<string, string>)
      }
    }
    if (message.values) {
      let values = this.elementValues.get(key)
      if (!values) this.elementValues.set(key, (values = {}))
      for (const [name, value] of entriesOf(message.values)) values[name] = value
    }
  }

  getActionsForElementId(elementId: string): Array<Record<string, unknown>> | null {
    if (!this.itemActions) return null
    const actions: Array<Record<string, unknown>> = []
    for (const itemId of this.elementItems.get(elementId) ?? []) {
      const itemActions = mapGet(this.itemActions, itemId)
      if (!Array.isArray(itemActions)) continue
      const objects = this.objectInfo.get(`${itemId}\u0000${elementId}`)
      for (const action of itemActions) {
        if (objects?.size) {
          for (const [objectId, object] of objects) {
            const next = { ...action, itemId: Number(itemId), ...object }
            const params = this.objectActionParams
              .get(`${itemId}\u0000${elementId}`)
              ?.get(objectId)
            if (params && typeof next.title === 'string') {
              for (const [placeholder, value] of Object.entries(params)) {
                next.title = next.title.replaceAll(placeholder, value)
              }
            }
            actions.push(next)
          }
        } else {
          actions.push({ ...action, itemId: Number(itemId) })
        }
      }
    }
    return actions.length ? actions : null
  }

  getObjectInfoForElementId(elementId: string, data: any): any {
    const objects = Array.from(this.elementItems.get(elementId) ?? [])
      .flatMap((itemId) => Array.from(this.objectInfo.get(`${itemId}\u0000${elementId}`)?.values() ?? []))
    if (!objects.length || !data) return data
    data.setField('objectTypeId', objects[0].objectTypeId ?? '')
    data.setField('objectId', objects[0].objectId ?? -1)
    data.setField('objectTitle', objects[0].objectTitle ?? '-')
    data.setField('objects', { rows: objects.map((object) => ({ ...object })) })
    return data
  }

  getValuesForElementId(elementId: string, data: any): any {
    for (const itemId of this.elementItems.get(elementId) ?? []) {
      const values = this.elementValues.get(`${itemId}\u0000${elementId}`)
      if (values) {
        for (const [name, value] of Object.entries(values)) {
          data?.setFieldAsObject?.(name, value)
        }
      }
    }
    return data
  }

  getElementById(id: string): any {
    const element = Array.from(this.root.querySelectorAll('[id]')).find((node) => node.id === id)
    return element ? decorateSvgElement(element) : null
  }

  querySelectorAll(selector: string): any[] {
    return Array.from(this.root.querySelectorAll(selector), decorateSvgElement)
  }

  createElementNS(namespace: string, tag: string, concreteTag?: string): any {
    if (!namespace.endsWith('/svg')) return null
    const element = this.root.ownerDocument.createElementNS(SVG_NAMESPACE, concreteTag ?? tag)
    return decorateSvgElement(element)
  }

  update(): void {}
}
